// Attendance review — scanner-import batches are staged here, reviewed
// (exceptions resolved, overtime approved) and then approved into payroll.
// Tabs: Batches (with inline batch drill-down) / Overtime / Exceptions / Policy.
import { formatDate } from '@/lib/orgFormat';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Clock,
  UploadCloud,
  ArrowLeft,
  ChevronRight,
  Inbox,
  AlertTriangle,
  Timer,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { DataState } from '@/features/shared/components/ui/DataState';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Switch } from '@/features/shared/components/ui/switch';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { UploadReminderCard } from './UploadReminderCard';
import { LiveConnectorCard } from './LiveConnectorCard';
import { OfficeNetworkPanel } from './OfficeNetworkPanel';
import { BackupCheckinToggle } from './BackupCheckinToggle';
import { outsideOffice } from './officeNetwork';
import { exceptionGroups } from './exceptionGroups';
import { AttendanceInsightsTab } from './AttendanceInsightsTab';
import { describeWorkingWeek, restDayWarning, workingDayIndexes } from './restDays';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import { cn } from '@/lib/utils';
import { useConfirm, usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { StatCard } from '@/features/payroll/components/StatCard';
import { getAllEmployees } from '@/lib/hrApi';
import * as api from '@/services/attendanceService';
import { formatMinutes } from '@/services/attendanceService';
import type {
  AttendanceBatch,
  AttendanceBatchDetail,
  AttendanceException,
  AttendancePolicy,
  ExceptionStatus,
  OvertimeRequest,
  OvertimeStatus,
} from '@/services/attendanceService';

// ---- Shared bits ------------------------------------------------------------

const CHIP_STYLES: Record<string, string> = {
  // batch
  staged: 'bg-warning-soft text-warning-strong',
  approved: 'bg-success-soft text-success-strong',
  discarded: 'bg-slate-100 text-slate-500',
  // days
  pending_review: 'bg-warning-soft text-warning-strong',
  excluded: 'bg-slate-100 text-slate-500',
  // exceptions
  open: 'bg-danger-soft text-danger-strong',
  resolved: 'bg-success-soft text-success-strong',
  ignored: 'bg-slate-100 text-slate-500',
  // overtime
  pending: 'bg-warning-soft text-warning-strong',
  rejected: 'bg-danger-soft text-danger-strong',
  // day kind
  workday: 'bg-slate-100 text-slate-600',
  rest_day: 'bg-info-soft text-info-strong',
  public_holiday: 'bg-purple-100 text-purple-700',
  // severity
  info: 'bg-info-soft text-info-strong',
  warning: 'bg-warning-soft text-warning-strong',
  critical: 'bg-danger-soft text-danger-strong',
};

const Chip: React.FC<{ value?: string; label?: string; className?: string }> = ({ value, label, className }) => (
  <Badge
    variant="secondary"
    className={cn('font-medium capitalize', CHIP_STYLES[value || ''] || 'bg-slate-100 text-slate-600', className)}
  >
    {label ?? (value || 'unknown').replace(/_/g, ' ')}
  </Badge>
);

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length <= 10 ? `${d}T00:00:00` : d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtTime = (v?: string | null) => {
  if (!v) return '—';
  if (v.includes('T')) {
    const dt = new Date(v);
    if (!Number.isNaN(dt.getTime())) return dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  // 'HH:MM[:SS]' strings — trim seconds.
  return v.length >= 5 ? v.slice(0, 5) : v;
};

/** Directory lookup: employee-service UUID -> display name (fallback: short id). */
const useEmployeeNames = () => {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let alive = true;
    getAllEmployees()
      .then((emps) => {
        if (!alive) return;
        const map: Record<string, string> = {};
        emps.forEach((e) => {
          map[e.id] = `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || e.id;
        });
        setNames(map);
      })
      .catch(() => {
        /* directory unavailable — fall back to shortened UUIDs */
      });
    return () => {
      alive = false;
    };
  }, []);
  return useCallback(
    (id?: string | null) => (id ? names[id] || `${id.slice(0, 8)}…` : '—'),
    [names],
  );
};

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

// ---- Overtime action dialogs ------------------------------------------------

const ApproveOtDialog: React.FC<{
  request: OvertimeRequest | null;
  nameOf: (id?: string | null) => string;
  onClose: () => void;
  onDone: () => void;
}> = ({ request, nameOf, onClose, onDone }) => {
  const { t } = useTranslation('attendance');
  const [minutes, setMinutes] = useState<string>('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setMinutes(request ? String(request.minutes_requested) : '');
  }, [request]);
  if (!request) return null;

  const submit = async () => {
    const m = Number(minutes);
    if (!Number.isFinite(m) || m <= 0 || m > request.minutes_requested) {
      toast.error(t('ot_minutes_invalid', 'Enter minutes between 1 and the requested amount'));
      return;
    }
    setBusy(true);
    try {
      const res = await api.approveOvertime(request.id, m !== request.minutes_requested ? m : undefined);
      toast.success(
        res.sent_to_payroll
          ? t('ot_approved_sent', 'Overtime approved and sent to payroll')
          : t('ot_approved', 'Overtime approved'),
      );
      onDone();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('err', 'Something went wrong'))));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('ot_approve_title', 'Approve overtime')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {t('ot_approve_desc', '{{name}} requested {{amount}} on {{date}}.', {
              name: nameOf(request.employee_id),
              amount: formatMinutes(request.minutes_requested),
              date: fmtDate(request.work_date),
            })}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="ot-minutes">{t('ot_minutes_label', 'Minutes to approve (reduce if needed)')}</Label>
            <Input
              id="ot-minutes"
              type="number"
              min={1}
              max={request.minutes_requested}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('ot_minutes_hint', '= {{amount}} at {{mult}}×', {
                amount: formatMinutes(Number(minutes) || 0),
                mult: request.multiplier,
              })}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('ot_approve_cta', 'Approve')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RejectOtDialog: React.FC<{
  request: OvertimeRequest | null;
  nameOf: (id?: string | null) => string;
  onClose: () => void;
  onDone: () => void;
}> = ({ request, nameOf, onClose, onDone }) => {
  const { t } = useTranslation('attendance');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setReason(''), [request]);
  if (!request) return null;

  const submit = async () => {
    if (!reason.trim()) {
      toast.error(t('ot_reason_required', 'A rejection reason is required'));
      return;
    }
    setBusy(true);
    try {
      await api.rejectOvertime(request.id, reason.trim());
      toast.success(t('ot_rejected', 'Overtime request rejected'));
      onDone();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('err', 'Something went wrong'))));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('ot_reject_title', 'Reject overtime')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {t('ot_reject_desc', 'Reject {{amount}} for {{name}} on {{date}}?', {
              amount: formatMinutes(request.minutes_requested),
              name: nameOf(request.employee_id),
              date: fmtDate(request.work_date),
            })}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="ot-reason">{t('ot_reason_label', 'Reason (required)')}</Label>
            <Textarea
              id="ot-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('ot_reason_placeholder', 'e.g. Overtime was not pre-authorised for this day')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" onClick={submit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('ot_reject_cta', 'Reject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Overtime table (used by the Overtime tab and the batch drill-down) ------

const OvertimeTable: React.FC<{
  items: OvertimeRequest[];
  nameOf: (id?: string | null) => string;
  canWrite: boolean;
  loading?: boolean;
  onApprove: (r: OvertimeRequest) => void;
  onReject: (r: OvertimeRequest) => void;
}> = ({ items, nameOf, canWrite, loading, onApprove, onReject }) => {
  const { t } = useTranslation('attendance');
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('col_employee', 'Employee')}</TableHead>
              <TableHead>{t('col_date', 'Date')}</TableHead>
              <TableHead className="text-right">{t('col_requested', 'Requested')}</TableHead>
              <TableHead className="text-right">{t('col_approved_min', 'Approved')}</TableHead>
              <TableHead>{t('col_multiplier', 'Rate')}</TableHead>
              <TableHead>{t('col_status', 'Status')}</TableHead>
              <TableHead className="text-right">{t('col_actions', 'Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading &&
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  {t('ot_empty', 'No overtime requests here.')}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium text-charcoal">{nameOf(r.employee_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(r.work_date)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMinutes(r.minutes_requested)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.minutes_approved != null ? formatMinutes(r.minutes_approved) : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 font-medium">
                      {Number(r.multiplier).toFixed(1)}×
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <Chip value={r.status} />
                      {r.status === 'rejected' && r.rejection_reason && (
                        <span className="text-xs text-muted-foreground max-w-[200px] truncate" title={r.rejection_reason}>
                          {r.rejection_reason}
                        </span>
                      )}
                      {r.sent_to_payroll_at && (
                        <span className="text-xs text-success">{t('ot_sent_payroll', 'Sent to payroll')}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {canWrite && r.status === 'pending' ? (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => onApprove(r)}>
                          {t('approve', 'Approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-danger hover:text-red-700"
                          onClick={() => onReject(r)}
                        >
                          {t('reject', 'Reject')}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// One screenful. A six-day batch is already 78 rows and a real month is
// thousands; an endless scroll is not a list anyone reads.
const DAYS_PAGE = 25;

// ---- Exceptions list (severity-coloured, resolve/ignore with note) ----------

const ExceptionList: React.FC<{
  items: AttendanceException[];
  nameOf: (id?: string | null) => string;
  canWrite: boolean;
  loading?: boolean;
  onChanged: () => void;
  /** What a bulk close applies to. Omitted = no bulk close offered. */
  bulkScope?: { batch_id?: string };
  /** Server-side 'type/severity' -> count for the WHOLE queue, not this page. */
  counts?: Record<string, number>;
  /** How many match the filter server-side, when more than the page shows. */
  total?: number;
}> = ({ items, nameOf, canWrite, loading, onChanged, bulkScope, counts, total }) => {
  const { t } = useTranslation('attendance');
  const promptText = usePrompt();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);

  const groups = useMemo(() => exceptionGroups(items, counts), [counts, items]);

  const bulkAct = async (
    group: { key: string; type: string; count: number },
    status: 'resolved' | 'ignored',
  ) => {
    const label = group.type.replace(/_/g, ' ');
    const note = await promptText({
      title:
        status === 'resolved'
          ? t('exc_bulk_resolve_title', 'Close {{count}} findings', { count: group.count })
          : t('exc_bulk_ignore_title', 'Ignore {{count}} findings', { count: group.count }),
      message: t(
        'exc_bulk_msg',
        'This closes every open "{{type}}" finding in scope — {{count}} of them. Say why: it is the only record of this decision.',
        { type: label, count: group.count },
      ),
      label: t('exc_bulk_reason', 'Reason'),
      required: true,
      multiline: true,
      confirmLabel: status === 'resolved' ? t('resolve_all', 'Resolve all') : t('ignore_all', 'Ignore all'),
      danger: true,
    });
    if (note == null) return;
    setBulkBusy(group.key);
    try {
      const r = await api.bulkResolveExceptions({ type: group.type, ...(bulkScope || {}) }, status, note.trim());
      toast.success(t('exc_bulk_ok', '{{count}} findings closed', { count: r.resolved }));
      onChanged();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('err', 'Something went wrong'))));
    } finally {
      setBulkBusy(null);
    }
  };

  const act = async (ex: AttendanceException, status: 'resolved' | 'ignored') => {
    // P3: a reason is required. Use the note typed on the row, or ask for one
    // (branded prompt — never a native dialog).
    let note = notes[ex.id]?.trim() || '';
    if (note.length < 5) {
      const asked = await promptText({
        title: status === 'resolved' ? t('exc_resolve_one', 'Resolve this finding') : t('exc_ignore_one', 'Ignore this finding'),
        message: t('exc_reason_needed', 'Say why (at least 5 characters). This note is the record of the decision.'),
        label: t('exc_bulk_reason', 'Reason'),
        required: true,
        multiline: true,
        minLength: 5,
      });
      if (asked == null) return;
      note = asked.trim();
    }
    setBusyId(ex.id);
    try {
      await api.resolveException(ex.id, status, note);
      toast.success(
        status === 'resolved'
          ? t('exc_resolved', 'Exception resolved')
          : t('exc_ignored', 'Exception ignored'),
      );
      onChanged();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('err', 'Something went wrong'))));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-success" />
          {t('exc_empty', 'No exceptions here — all clear.')}
        </CardContent>
      </Card>
    );
  }

  const BORDER: Record<string, string> = {
    info: 'border-l-info',
    warning: 'border-l-warning',
    critical: 'border-l-danger',
  };

  return (
    <div className="space-y-3">
      {canWrite && bulkScope && groups.length > 0 && (
        <Card className="border-dashed">
          <CardContent className="space-y-2 p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('exc_bulk_heading', 'Close a whole kind at once')}
            </p>
            {typeof total === 'number' && total > items.length && (
              <p className="text-xs text-muted-foreground">
                {t('exc_window', 'Showing {{shown}} of {{total}} — the actions below cover all of them.', {
                  shown: items.length,
                  total,
                })}
              </p>
            )}
            {groups.map((g) => (
              <div key={g.key} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-charcoal">
                  <strong>{g.count}</strong> × {g.type.replace(/_/g, ' ')}{' '}
                  <Chip value={g.severity} />
                </span>
                <span className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkBusy === g.key}
                    onClick={() => bulkAct(g, 'resolved')}
                  >
                    {t('resolve_all', 'Resolve all')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-muted-foreground"
                    disabled={bulkBusy === g.key}
                    onClick={() => bulkAct(g, 'ignored')}
                  >
                    {t('ignore_all', 'Ignore all')}
                  </Button>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {items.map((ex) => (
        <Card key={ex.id} className={cn('border-l-4', BORDER[ex.severity] || 'border-l-slate-300')}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip value={ex.severity} />
                  <Chip value={ex.status} />
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {ex.type.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-charcoal">{ex.message}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {nameOf(ex.employee_id)} · {fmtDate(ex.work_date)}
                </p>
                {ex.resolution_note && (
                  <p className="mt-1 text-xs text-muted-foreground italic">
                    {t('exc_note_prefix', 'Note')}: {ex.resolution_note}
                  </p>
                )}
              </div>
              {canWrite && ex.status === 'open' && (
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                  <Input
                    className="h-9 w-full sm:w-52"
                    placeholder={t('exc_note_placeholder', 'Optional note…')}
                    value={notes[ex.id] || ''}
                    onChange={(e) => setNotes((n) => ({ ...n, [ex.id]: e.target.value }))}
                  />
                  <Button size="sm" variant="outline" disabled={busyId === ex.id} onClick={() => act(ex, 'resolved')}>
                    {t('resolve', 'Resolve')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-muted-foreground"
                    disabled={busyId === ex.id}
                    onClick={() => act(ex, 'ignored')}
                  >
                    {t('ignore', 'Ignore')}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ---- Batch drill-down ---------------------------------------------------------

const BatchDetail: React.FC<{
  batchId: string;
  nameOf: (id?: string | null) => string;
  canWrite: boolean;
  onBack: () => void;
  onChanged: () => void;
}> = ({ batchId, nameOf, canWrite, onBack, onChanged }) => {
  const { t } = useTranslation('attendance');
  const confirm = useConfirm();
  const [batch, setBatch] = useState<AttendanceBatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [otApprove, setOtApprove] = useState<OvertimeRequest | null>(null);
  const [otReject, setOtReject] = useState<OvertimeRequest | null>(null);
  const [dayFilter, setDayFilter] = useState('');
  const [daysShown, setDaysShown] = useState(DAYS_PAGE);

  const load = useCallback(() => {
    api
      .getBatch(batchId)
      .then(setBatch)
      .catch((e) => toast.error(errMsg(e, t('err', 'Something went wrong'))))
      .finally(() => setLoading(false));
  }, [batchId, t]);
  useEffect(load, [load]);

  const openCritical = useMemo(
    () => (batch?.exceptions || []).filter((e) => e.status === 'open' && e.severity === 'critical').length,
    [batch],
  );

  const visibleDays = useMemo(() => {
    const rows = batch?.days || [];
    const q = dayFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((d) => nameOf(d.employee_id).toLowerCase().includes(q));
  }, [batch, dayFilter, nameOf]);

  const doApprove = async () => {
    if (!batch) return;
    const ok = await confirm({
      title: t('approve_batch_title', 'Approve batch?'),
      message: t(
        'approve_batch_msg',
        'Approve "{{file}}" ({{from}} – {{to}})? Approved days become final and approved overtime is sent to payroll.',
        { file: batch.file_name, from: fmtDate(batch.period_start), to: fmtDate(batch.period_end) },
      ),
      confirmLabel: t('approve_batch_cta', 'Approve batch'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.approveBatch(batch.id);
      toast.success(
        t('approve_batch_ok', '{{days}} days approved · {{sent}} overtime requests sent to payroll', {
          days: res.approved_days,
          sent: res.overtime_sent,
        }),
      );
      load();
      onChanged();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        toast.error(
          `${errMsg(e, t('approve_batch_conflict', 'Batch cannot be approved yet'))} — ${t(
            'approve_batch_hint',
            'resolve the critical exceptions first',
          )}`,
          { duration: 6000 },
        );
      } else {
        toast.error(errMsg(e, t('err', 'Something went wrong')));
      }
    } finally {
      setBusy(false);
    }
  };

  const doRollback = async () => {
    if (!batch) return;
    // Deleting raw scans is the point when the wrong file was imported — the
    // scans are the personal data — so the dialog says exactly what goes and
    // that it cannot be undone.
    const ok = await confirm({
      title: t('rollback_title', 'Roll this batch back?'),
      message: t('rollback_msg', {
        defaultValue:
          'This permanently deletes everything this import brought in: {{rows}} scan row(s), {{days}} day(s) and {{exceptions}} finding(s). It cannot be undone. Nothing from any other batch is touched.',
        rows: batch.total_rows,
        days: (batch.days || []).length,
        exceptions: (batch.exceptions || []).length,
      }),
      confirmLabel: t('rollback', 'Roll back'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.rollbackBatch(batch.id);
      toast.success(t('rollback_ok', {
        defaultValue: 'Batch rolled back — {{days}} day(s) and {{exceptions}} finding(s) removed.',
        days: r.days_deleted, exceptions: r.exceptions_deleted,
      }));
      onChanged();
      onBack();
    } catch (e: any) {
      // A 409 carries the reason it was refused; show that, not "went wrong".
      toast.error(getApiErrorMessage(e, errMsg(e, t('err', 'Something went wrong'))));
    } finally {
      setBusy(false);
    }
  };

  const doDiscard = async () => {
    if (!batch) return;
    const ok = await confirm({
      title: t('discard_batch_title', 'Discard batch?'),
      message: t('discard_batch_msg', 'Discard "{{file}}"? Its staged days and exceptions will be dropped.', {
        file: batch.file_name,
      }),
      confirmLabel: t('discard_batch_cta', 'Discard'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.discardBatch(batch.id);
      toast.success(t('discard_batch_ok', 'Batch discarded'));
      onChanged();
      onBack();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('err', 'Something went wrong'))));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }
  if (!batch) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> {t('back_to_batches', 'Back to batches')}
        </Button>
        <p className="text-muted-foreground">{t('batch_not_found', 'Batch not found.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2 mb-1 gap-1 text-muted-foreground">
            <ArrowLeft className="h-4 w-4" /> {t('back_to_batches', 'Back to batches')}
          </Button>
          <h2 className="flex flex-wrap items-center gap-2 text-lg font-bold text-charcoal">
            <span className="truncate">{batch.file_name}</span>
            <Chip value={batch.status} />
          </h2>
          <p className="text-sm text-muted-foreground">
            {fmtDate(batch.period_start)} – {fmtDate(batch.period_end)} ·{' '}
            {t('batch_meta', '{{matched}}/{{total}} rows matched · {{unmatched}} unmatched · source {{source}}', {
              matched: batch.matched_rows,
              total: batch.total_rows,
              unmatched: batch.unmatched_rows,
              source: batch.source,
            })}
          </p>
          {batch.approved_at && (
            <p className="text-xs text-success">{t('batch_approved_at', 'Approved {{when}}', { when: fmtDate(batch.approved_at) })}</p>
          )}
        </div>
        {canWrite && batch.status !== 'approved' && (
          <div className="flex gap-2">
            {/* Roll back is offered on ANY unapproved batch, not just a staged
                one: a `no_changes` batch that matched nobody still left 527
                findings behind, and Discard only sets a batch aside. */}
            <Button variant="outline" className="text-danger hover:text-red-700" disabled={busy} onClick={doRollback}>
              {t('rollback', 'Roll back')}
            </Button>
            {batch.status === 'staged' && (
              <>
                <Button variant="outline" className="text-danger hover:text-red-700" disabled={busy} onClick={doDiscard}>
                  {t('discard', 'Discard')}
                </Button>
                <Button disabled={busy} onClick={doApprove} className="gap-2">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('approve_batch_cta', 'Approve batch')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {openCritical > 0 && batch.status === 'staged' && (
        <div className="flex items-center gap-2 rounded-xl border border-danger bg-danger-soft px-4 py-3 text-sm text-danger-strong">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t('critical_blocker', '{{count}} open critical exception(s) must be resolved before this batch can be approved.', {
            count: openCritical,
          })}
        </div>
      )}

      <Tabs defaultValue="days">
        <TabsList>
          <TabsTrigger value="days">
            {t('tab_days', 'Days')} ({batch.days.length})
          </TabsTrigger>
          <TabsTrigger value="exceptions">
            {/* "Exceptions (7)" next to a list column reading 56 for the same
                batch is the same word counting two different things. Say which. */}
            {t('tab_exceptions_open', 'Open exceptions')} (
            {batch.exceptions.filter((e) => e.status === 'open').length})
          </TabsTrigger>
          <TabsTrigger value="overtime">
            {t('tab_overtime', 'Overtime')} ({batch.overtime_requests.filter((r) => r.status === 'pending').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="days" className="mt-4">
          {/* 78 rows for a six-day batch, and a real month is 2,000. A name
              filter and a capped first page beat an endless scroll — and the
              count says what is being hidden, never just "…". */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Input
              className="h-9 w-full sm:w-64"
              placeholder={t('days_filter', 'Filter by employee…') as string}
              value={dayFilter}
              onChange={(e) => { setDayFilter(e.target.value); setDaysShown(DAYS_PAGE); }}
            />
            <span className="text-xs text-muted-foreground tabular-nums">
              {t('days_showing', 'Showing {{shown}} of {{total}}', {
                shown: Math.min(visibleDays.length, daysShown),
                total: batch.days.length,
              })}
            </span>
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('col_employee', 'Employee')}</TableHead>
                    <TableHead>{t('col_date', 'Date')}</TableHead>
                    <TableHead>{t('col_in_out', 'In / Out')}</TableHead>
                    <TableHead className="text-right">{t('col_net', 'Net')}</TableHead>
                    <TableHead className="text-right">{t('col_regular', 'Regular')}</TableHead>
                    <TableHead className="text-right">{t('col_ot', 'OT (pot / appr)')}</TableHead>
                    <TableHead>{t('col_day_kind', 'Day')}</TableHead>
                    <TableHead className="text-right">{t('col_late_early', 'Late / Early')}</TableHead>
                    <TableHead>{t('col_status', 'Status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleDays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                        {batch.days.length === 0
                          ? t('days_empty', 'No attendance days in this batch.')
                          : t('days_no_match', 'No days match that name.')}
                      </TableCell>
                    </TableRow>
                  )}
                  {visibleDays.slice(0, daysShown).map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium text-charcoal">{nameOf(d.employee_id)}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(d.work_date)}</TableCell>
                      <TableCell className="tabular-nums text-muted-foreground">
                        {fmtTime(d.clock_in)} → {fmtTime(d.clock_out)}
                        {d.punch_count > 2 && (
                          <span className="ml-1 text-xs">({t('punches', '{{count}} punches', { count: d.punch_count })})</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-charcoal">
                        {formatMinutes(d.net_minutes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatMinutes(d.regular_minutes)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {d.potential_ot_minutes > 0 || d.approved_ot_minutes > 0
                          ? `${formatMinutes(d.potential_ot_minutes)} / ${formatMinutes(d.approved_ot_minutes)}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip value={d.day_kind} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {d.late_minutes > 0 || d.early_departure_minutes > 0 ? (
                          <span className="text-warning">
                            {d.late_minutes > 0 ? formatMinutes(d.late_minutes) : '—'}
                            {' / '}
                            {d.early_departure_minutes > 0 ? formatMinutes(d.early_departure_minutes) : '—'}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip value={d.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {visibleDays.length > daysShown && (
            <div className="mt-3 text-center">
              <Button variant="outline" size="sm" onClick={() => setDaysShown((n) => n + DAYS_PAGE)}>
                {t('days_more', 'Show {{n}} more', {
                  n: Math.min(DAYS_PAGE, visibleDays.length - daysShown),
                })}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="exceptions" className="mt-4">
          <ExceptionList
            items={batch.exceptions}
            nameOf={nameOf}
            canWrite={canWrite}
            bulkScope={{ batch_id: batch.id }}
            onChanged={() => {
              load();
              onChanged();
            }}
          />
        </TabsContent>

        <TabsContent value="overtime" className="mt-4">
          <OvertimeTable
            items={batch.overtime_requests}
            nameOf={nameOf}
            canWrite={canWrite}
            onApprove={setOtApprove}
            onReject={setOtReject}
          />
        </TabsContent>
      </Tabs>

      <ApproveOtDialog
        request={otApprove}
        nameOf={nameOf}
        onClose={() => setOtApprove(null)}
        onDone={() => {
          setOtApprove(null);
          load();
          onChanged();
        }}
      />
      <RejectOtDialog
        request={otReject}
        nameOf={nameOf}
        onClose={() => setOtReject(null)}
        onDone={() => {
          setOtReject(null);
          load();
          onChanged();
        }}
      />
    </div>
  );
};

// ---- Batches tab ---------------------------------------------------------------

const BatchesTab: React.FC<{
  nameOf: (id?: string | null) => string;
  canWrite: boolean;
  refreshKey: number;
  onGlobalChanged: () => void;
}> = ({ nameOf, canWrite, refreshKey, onGlobalChanged }) => {
  const { t } = useTranslation('attendance');
  const [batches, setBatches] = useState<AttendanceBatch[]>([]);
  const [batchesError, setBatchesError] = useState(false);
  const [retryN, setRetryN] = useState(0);
  const [openExceptions, setOpenExceptions] = useState<number | null>(null);
  const [pendingOt, setPendingOt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // `total`, not the page length: the list is windowed, so counting the rows
    // it returned reported 500 open exceptions when there were 729.
    Promise.allSettled([api.listBatches(), api.queryExceptions({ status: 'open', limit: 1 }), api.listOvertime('pending')]).then(
      ([b, e, o]) => {
        if (!alive) return;
        setBatches(b.status === 'fulfilled' ? b.value : []);
        setBatchesError(b.status === 'rejected');
        setOpenExceptions(e.status === 'fulfilled' ? e.value.total : null);
        setPendingOt(o.status === 'fulfilled' ? o.value.length : null);
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [refreshKey, retryN]);

  const staged = useMemo(() => batches.filter((b) => b.status === 'staged').length, [batches]);

  if (selectedId) {
    return (
      <BatchDetail
        batchId={selectedId}
        nameOf={nameOf}
        canWrite={canWrite}
        onBack={() => setSelectedId(null)}
        onChanged={onGlobalChanged}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Inbox} label={t('stat_staged', 'Staged batches')} value={String(staged)} tone="info" />
        <StatCard
          icon={AlertTriangle}
          label={t('stat_open_exceptions', 'Open exceptions')}
          value={openExceptions == null ? '—' : String(openExceptions)}
          tone={openExceptions ? 'danger' : 'success'}
        />
        <StatCard
          icon={Timer}
          label={t('stat_pending_ot', 'Pending overtime')}
          value={pendingOt == null ? '—' : String(pendingOt)}
          tone={pendingOt ? 'default' : 'success'}
        />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('col_file', 'File')}</TableHead>
                <TableHead>{t('col_period', 'Period')}</TableHead>
                <TableHead className="text-right">{t('col_rows', 'Rows (matched / unmatched)')}</TableHead>
                <TableHead className="text-right" title={t('col_exceptions_hint', 'Raised by this import, including any since resolved') as string}>
                  {t('col_exceptions_raised', 'Exceptions raised')}
                </TableHead>
                <TableHead>{t('col_status', 'Status')}</TableHead>
                <TableHead>{t('col_created', 'Created')}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              {!loading && batchesError && (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    <DataState variant="error" onRetry={() => setRetryN((n) => n + 1)}
                      message={t('batches_load_failed', 'Couldn’t load attendance batches.') as string}
                      retryLabel={t('retry', 'Retry') as string} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && !batchesError && batches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                    {t('batches_empty', 'No attendance batches yet — import a scanner file to get started.')}
                  </TableCell>
                </TableRow>
              )}
              {!loading && !batchesError &&
                batches.map((b) => (
                  <TableRow key={b.id} className="cursor-pointer" onClick={() => setSelectedId(b.id)}>
                    <TableCell>
                      <div className="font-medium text-charcoal">{b.file_name}</div>
                      <div className="text-xs capitalize text-muted-foreground">{b.source}</div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {fmtDate(b.period_start)} – {fmtDate(b.period_end)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.matched_rows}
                      <span className="text-muted-foreground"> / </span>
                      <span className={b.unmatched_rows > 0 ? 'font-medium text-warning' : 'text-muted-foreground'}>
                        {b.unmatched_rows}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.exception_count > 0 ? (
                        <span className="font-medium text-danger">{b.exception_count}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip value={b.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(b.created_at)}</TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// ---- Overtime tab ----------------------------------------------------------------

const OT_FILTERS: { value: OvertimeStatus | 'all'; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const OvertimeTab: React.FC<{
  nameOf: (id?: string | null) => string;
  canWrite: boolean;
  refreshKey: number;
  onGlobalChanged: () => void;
}> = ({ nameOf, canWrite, refreshKey, onGlobalChanged }) => {
  const { t } = useTranslation('attendance');
  const [filter, setFilter] = useState<OvertimeStatus | 'all'>('pending');
  const [items, setItems] = useState<OvertimeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [otApprove, setOtApprove] = useState<OvertimeRequest | null>(null);
  const [otReject, setOtReject] = useState<OvertimeRequest | null>(null);
  const [localBump, setLocalBump] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .listOvertime(filter)
      .then((r) => { if (alive) { setItems(r); setError(false); } })
      .catch(() => { if (alive) { setItems([]); setError(true); } })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filter, refreshKey, localBump]);

  const done = () => {
    setOtApprove(null);
    setOtReject(null);
    setLocalBump((n) => n + 1);
    onGlobalChanged();
  };

  return (
    <div className="space-y-4">
      {error && <DataState variant="error" compact onRetry={() => setLocalBump((n) => n + 1)} message={t('ot_load_failed', 'Couldn’t load overtime.') as string} retryLabel={t('retry', 'Retry') as string} />}
      <div className="flex flex-wrap gap-2">
        {OT_FILTERS.map((f) => (
          <button
            type="button"
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              filter === f.value
                ? 'bg-charcoal text-white'
                : 'border border-border bg-card text-muted-foreground hover:bg-muted',
            )}
          >
            {t(`ot_filter_${f.value}`, f.label)}
          </button>
        ))}
      </div>
      <OvertimeTable
        items={items}
        nameOf={nameOf}
        canWrite={canWrite}
        loading={loading}
        onApprove={setOtApprove}
        onReject={setOtReject}
      />
      <ApproveOtDialog request={otApprove} nameOf={nameOf} onClose={() => setOtApprove(null)} onDone={done} />
      <RejectOtDialog request={otReject} nameOf={nameOf} onClose={() => setOtReject(null)} onDone={done} />
    </div>
  );
};

// ---- Exceptions tab ----------------------------------------------------------------

const EXC_FILTERS: { value: ExceptionStatus | 'all'; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'ignored', label: 'Ignored' },
  { value: 'all', label: 'All' },
];

const ExceptionsTab: React.FC<{
  nameOf: (id?: string | null) => string;
  canWrite: boolean;
  refreshKey: number;
  onGlobalChanged: () => void;
}> = ({ nameOf, canWrite, refreshKey, onGlobalChanged }) => {
  const { t } = useTranslation('attendance');
  const [filter, setFilter] = useState<ExceptionStatus | 'all'>('open');
  const [severity, setSeverity] = useState<'all' | 'info' | 'warning' | 'critical'>('all');
  const [items, setItems] = useState<AttendanceException[]>([]);
  const [queue, setQueue] = useState<{ total: number; counts: Record<string, number> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [localBump, setLocalBump] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .queryExceptions({ status: filter })
      .then((r) => {
        if (alive) {
          setItems(r.exceptions);
          setQueue({ total: r.total, counts: r.counts });
          setError(false);
        }
      })
      .catch(() => { if (alive) { setItems([]); setQueue(null); setError(true); } })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filter, refreshKey, localBump]);

  const visible = useMemo(
    () => (severity === 'all' ? items : items.filter((e) => e.severity === severity)),
    [items, severity],
  );

  return (
    <div className="space-y-4">
      {error && <DataState variant="error" compact onRetry={() => setLocalBump((n) => n + 1)} message={t('exc_load_failed', 'Couldn’t load exceptions.') as string} retryLabel={t('retry', 'Retry') as string} />}
      <div className="flex flex-wrap items-center gap-2">
        {EXC_FILTERS.map((f) => (
          <button
            type="button"
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              filter === f.value
                ? 'bg-charcoal text-white'
                : 'border border-border bg-card text-muted-foreground hover:bg-muted',
            )}
          >
            {t(`exc_filter_${f.value}`, f.label)}
          </button>
        ))}
        <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
        {(['all', 'critical', 'warning', 'info'] as const).map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => setSeverity(s)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors',
              severity === s
                ? 'bg-orange-500/15 text-orange-700 ring-1 ring-orange-400'
                : 'border border-border bg-card text-muted-foreground hover:bg-muted',
            )}
          >
            {t(`severity_${s}`, s === 'all' ? 'Any severity' : s)}
          </button>
        ))}
      </div>
      <ExceptionList
        items={visible}
        nameOf={nameOf}
        canWrite={canWrite}
        loading={loading}
        // Bulk close is only ever offered on the OPEN queue — closing what is
        // already closed is not an action, and the backend scopes to open too.
        bulkScope={filter === 'open' ? {} : undefined}
        counts={severity === 'all' ? queue?.counts : undefined}
        total={severity === 'all' ? queue?.total : undefined}
        onChanged={() => {
          setLocalBump((n) => n + 1);
          onGlobalChanged();
        }}
      />
    </div>
  );
};

// ---- Policy tab ----------------------------------------------------------------

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const OVERNIGHT_OPTIONS: Array<{ value: api.OvernightMode; label: string; hint: string }> = [
  {
    value: 'split',
    label: 'Keep each calendar day separate',
    hint: 'A 22:00–06:00 shift shows as two days, each missing a punch. Choose this only if nobody works nights.',
  },
  {
    value: 'stitch_start',
    label: 'Count the shift on the day it started',
    hint: 'Monday 22:00 to Tuesday 06:00 is paid and shown on Monday.',
  },
  {
    value: 'stitch_end',
    label: 'Count the shift on the day it ended',
    hint: 'Monday 22:00 to Tuesday 06:00 is paid and shown on Tuesday.',
  },
];

const PolicyTab: React.FC<{ canWrite: boolean }> = ({ canWrite }) => {
  const { t } = useTranslation('attendance');
  const [policy, setPolicy] = useState<AttendancePolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getPolicy()
      .then(setPolicy)
      .catch((e) => toast.error(errMsg(e, t('err', 'Something went wrong'))))
      .finally(() => setLoading(false));
  }, [t]);

  const set = <K extends keyof AttendancePolicy>(k: K, v: AttendancePolicy[K]) =>
    setPolicy((p) => (p ? { ...p, [k]: v } : p));

  const toggleRestDay = (d: number) =>
    setPolicy((p) => {
      if (!p) return p;
      const has = p.rest_days.includes(d);
      return { ...p, rest_days: has ? p.rest_days.filter((x) => x !== d) : [...p.rest_days, d].sort() };
    });

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const updated = await api.updatePolicy(policy);
      setPolicy(updated);
      toast.success(t('policy_saved', 'Attendance policy saved'));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('err', 'Something went wrong'))));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (!policy) return <p className="text-muted-foreground">{t('policy_unavailable', 'Attendance policy is unavailable.')}</p>;

  const hoursOf = (mins: number) => (mins ? String(Math.round((mins / 60) * 100) / 100) : '0');
  const numInput = (v: string) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  return (
    <div className="max-w-3xl space-y-5">
      <p className="text-sm text-muted-foreground">
        {t('policy_explainer', 'These rules are applied when scanner files are imported.')}
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('policy_workday', 'Working day')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="p-start">{t('policy_work_start', 'Work start')}</Label>
            <Input
              id="p-start"
              type="time"
              value={policy.work_start}
              disabled={!canWrite}
              onChange={(e) => set('work_start', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-end">{t('policy_work_end', 'Work end')}</Label>
            <Input
              id="p-end"
              type="time"
              value={policy.work_end}
              disabled={!canWrite}
              onChange={(e) => set('work_end', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-grace">{t('policy_grace', 'Late grace (minutes)')}</Label>
            <Input
              id="p-grace"
              type="number"
              min={0}
              value={policy.grace_minutes}
              disabled={!canWrite}
              onChange={(e) => set('grace_minutes', numInput(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-standard">{t('policy_standard_day', 'Standard day (hours)')}</Label>
            <Input
              id="p-standard"
              type="number"
              min={0}
              step={0.25}
              value={hoursOf(policy.standard_daily_minutes)}
              disabled={!canWrite}
              onChange={(e) => set('standard_daily_minutes', Math.round(numInput(e.target.value) * 60))}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>{t('policy_rest_days', 'Rest days')}</Label>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((d, i) => {
                const active = policy.rest_days.includes(i);
                return (
                  <button
                    type="button"
                    key={d}
                    disabled={!canWrite}
                    onClick={() => toggleRestDay(i)}
                    className={cn(
                      'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-60',
                      active
                        ? 'bg-charcoal text-white'
                        : 'border border-border bg-card text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {t(`day_${i}`, d)}
                  </button>
                );
              })}
            </div>
            {/* The consequence, before it is saved. The control was always
                labelled "Rest days" and a tenant still saved Mon–Fri into it,
                which silently took September from 25 working days to 7. A
                better noun was not going to fix that; showing the result is. */}
            <p className="text-xs text-muted-foreground">
              {t('policy_working_week', 'Working days: {{days}} — {{count}} a week.', {
                days: describeWorkingWeek(policy.rest_days) || t('policy_working_none', 'none'),
                count: workingDayIndexes(policy.rest_days).length,
              })}{' '}
              {t('policy_rest_days_hint', 'Work on a rest day counts as overtime.')}
            </p>
            {restDayWarning(policy.rest_days) && (
              <p className="flex items-start gap-1.5 text-xs text-warning" data-testid="rest-days-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{restDayWarning(policy.rest_days)}</span>
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('policy_lunch', 'Lunch break')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="p-lunch">{t('policy_lunch_minutes', 'Lunch break (minutes)')}</Label>
            <Input
              id="p-lunch"
              type="number"
              min={0}
              value={policy.lunch_minutes}
              disabled={!canWrite}
              onChange={(e) => set('lunch_minutes', numInput(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-lunch-after">{t('policy_lunch_after', 'Deduct lunch after (minutes worked)')}</Label>
            <Input
              id="p-lunch-after"
              type="number"
              min={0}
              value={policy.lunch_after_minutes}
              disabled={!canWrite}
              onChange={(e) => set('lunch_after_minutes', numInput(e.target.value))}
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3 sm:col-span-1">
            <div>
              <p className="text-sm font-medium text-charcoal">{t('policy_lunch_paid', 'Lunch is paid')}</p>
              <p className="text-xs text-muted-foreground">
                {t('policy_lunch_paid_hint', 'Paid lunch is not deducted from worked time.')}
              </p>
            </div>
            <Switch checked={policy.lunch_paid} disabled={!canWrite} onCheckedChange={(v) => set('lunch_paid', v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3 sm:col-span-1">
            <div>
              <p className="text-sm font-medium text-charcoal">{t('policy_auto_lunch', 'Auto-deduct lunch')}</p>
              <p className="text-xs text-muted-foreground">
                {t('policy_auto_lunch_hint', 'Deduct the lunch break when no break punch exists.')}
              </p>
            </div>
            <Switch
              checked={policy.auto_lunch_deduct}
              disabled={!canWrite}
              onCheckedChange={(v) => set('auto_lunch_deduct', v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('policy_overtime', 'Overtime')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium text-charcoal">{t('policy_ot_approval', 'Overtime requires approval')}</p>
              <p className="text-xs text-muted-foreground">
                {t('policy_ot_approval_hint', 'Extra hours only reach payroll once a manager approves them.')}
              </p>
            </div>
            <Switch
              checked={policy.overtime_requires_approval}
              disabled={!canWrite}
              onCheckedChange={(v) => set('overtime_requires_approval', v)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-ot-cap">{t('policy_ot_cap', 'Daily overtime cap (hours)')}</Label>
            <Input
              id="p-ot-cap"
              type="number"
              min={0}
              step={0.25}
              value={hoursOf(policy.overtime_daily_cap_minutes)}
              disabled={!canWrite}
              onChange={(e) => set('overtime_daily_cap_minutes', Math.round(numInput(e.target.value) * 60))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('policy_live', 'Night shifts and same-day alerts')}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t(
              'policy_live_hint',
              'These apply to punches from the live scanner connector. Imported scanner files are not affected.',
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <fieldset className="space-y-2" disabled={!canWrite}>
            <legend className="text-sm font-medium text-charcoal">
              {t('policy_overnight', 'Shifts that cross midnight')}
            </legend>
            <div className="grid grid-cols-1 gap-2">
              {OVERNIGHT_OPTIONS.map((o) => {
                const active = (policy.overnight_mode ?? 'split') === o.value;
                return (
                  <label
                    key={o.value}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition-colors',
                      active ? 'border-charcoal bg-muted' : 'border-border hover:bg-muted',
                      !canWrite && 'cursor-default opacity-60',
                    )}
                  >
                    <input
                      type="radio"
                      name="overnight-mode"
                      className="mt-1"
                      value={o.value}
                      checked={active}
                      onChange={() => set('overnight_mode', o.value)}
                    />
                    <span>
                      <span className="block text-sm font-medium text-charcoal">
                        {t(`policy_overnight_${o.value}`, o.label)}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {t(`policy_overnight_${o.value}_hint`, o.hint)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          {policy.overnight_mode && policy.overnight_mode !== 'split' && (
            <div className="space-y-1.5 sm:max-w-xs">
              <Label htmlFor="p-overnight-max">{t('policy_overnight_max', 'Longest shift to join up (hours)')}</Label>
              <Input
                id="p-overnight-max"
                type="number"
                min={8}
                max={24}
                value={policy.overnight_max_shift_hours ?? 16}
                disabled={!canWrite}
                onChange={(e) => set('overnight_max_shift_hours', Math.round(numInput(e.target.value)))}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'policy_overnight_max_hint',
                  'Punches further apart are left as two incomplete days, not joined into one long shift.',
                )}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium text-charcoal">{t('policy_notify_late', 'Tell the manager about late arrivals')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('policy_notify_late_hint', 'Sent the same day, on working days, after the late grace.')}
                </p>
              </div>
              <Switch
                checked={!!policy.notify_late}
                disabled={!canWrite}
                onCheckedChange={(v) => set('notify_late', v)}
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium text-charcoal">
                  {t('policy_notify_early', 'Tell the manager about early departures')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('policy_notify_early_hint', 'Sent the same day, on working days.')}
                </p>
              </div>
              <Switch
                checked={!!policy.notify_early_departure}
                disabled={!canWrite}
                onCheckedChange={(v) => set('notify_early_departure', v)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {policy.measurement_start_date
              ? t('policy_go_live', 'Nobody is told about days before the go-live date, {{date}}.', {
                  date: formatDate(policy.measurement_start_date),
                })
              : t('policy_go_live_unset', 'No go-live date yet — it is set when the scanner connector is enrolled.')}
          </p>
        </CardContent>
      </Card>

      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('policy_save', 'Save policy')}
          </Button>
        </div>
      )}
    </div>
  );
};

// ---- Presence capture modes (per branch) ----------------------------------------

const MODE_OPTIONS: Array<{ value: api.CaptureMode; label: string; hint: string }> = [
  { value: 'biometric', label: 'Biometric / scanner', hint: 'Presence comes from the scanner; self check-in is off unless backup is turned on below.' },
  { value: 'self_checkin', label: 'Self check-in', hint: 'No scanner at this office — employees check in/out from the app.' },
  { value: 'untracked', label: 'Not tracked', hint: 'Presence assumed per schedule; only leave and assignments are recorded.' },
];

export const CaptureModesCard: React.FC<{ canWrite: boolean }> = ({ canWrite }) => {
  const { t } = useTranslation('attendance');
  const [defaultMode, setDefaultMode] = useState<api.CaptureMode>('biometric');
  const [rows, setRows] = useState<api.CapturePolicyRow[]>([]);
  const [branches, setBranches] = useState<Array<{ id: string; name: string }>>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const confirm = useConfirm();
  // ⚠️ BACKUP CAME BACK ON BY ITSELF (DEF-0mvooe9wg002-5538bd, QA 2026-10-25).
  // The checkbox drew the SERVER value, so for the whole save (PUT + the
  // confirming read — a second or two from the US to the jnb region) an
  // untick snapped straight back to TICKED: "my untick didn't take". The next
  // activation of the box — a re-click, a double-click, Space on the
  // still-focused box — then landed once the read had unticked it, and was
  // an ON save with its own "turned on" toast. Dev showed exactly that: one
  // row, "on since" == updated_at, by the same user, right after the OFF.
  // Now: the box shows the choice being saved (`pendingBackup`), a scope with
  // a save or question open ignores further activations (a ref — `disabled`
  // only applies after the next render), turning ON always asks first, and
  // each write names the version it was made from (`base_updated_at`) so a
  // stale view is refused by the server instead of silently undoing an OFF.
  const [pendingBackup, setPendingBackup] = useState<Record<string, boolean>>({});
  const backupOpen = useRef<Set<string>>(new Set());
  // ⚠️ NO DATA IS NOT "OFF" (QA B3 retest). Before the first read lands, or
  // when a read fails, the card used to render its initial state — company
  // default "biometric", no rows — so every toggle showed OFF, which reads as
  // "the setting did not survive". Until a read succeeds the card shows
  // loading / the error with Retry, and no toggle at all.
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');

  const load = () =>
    api.listCapturePolicies().then(
      (r) => { setDefaultMode(r.default_mode); setRows(r.policies); setLoadState('ready'); return r; },
      (e) => {
        setLoadError(getApiErrorMessage(e, t('capture_load_failed', 'Couldn’t load the presence capture settings.')));
        setLoadState('error');
        throw e;
      },
    );
  const reload = () => { load().catch(() => {}); };
  const retryLoad = () => { setLoadState('loading'); reload(); };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    import('@/lib/hrApi').then((m) => (m as any).listBranches?.()).then((items: any) =>
      setBranches((items || []).map((b: any) => ({ id: b.id, name: b.name })))).catch(() => {});
  }, []);

  const modeFor = (branchId: string): api.CaptureMode =>
    rows.find((r) => r.branch_id === branchId)?.mode ?? defaultMode;

  // ⚠️ A success toast is a claim that the setting is stored (QA B3,
  // 2026-10-24: "turned on", then gone after sign-out). So the toast waits
  // for a FRESH read of the policies and only appears when that read shows
  // the change; anything else says it did not save.
  const confirmSaved = async (branchId: string | null, holds: (r?: api.CapturePolicyRow) => boolean) => {
    const fresh = await load();
    return holds(fresh.policies.find((r) => (r.branch_id ?? null) === branchId));
  };
  const notSaved = () => toast.error(
    t('capture_not_saved', 'The change did not save. What you see now is the setting in force — please try again.'),
    { duration: 8000 },
  );

  // The row a scope's toggle shows: its own, else (an office) the company default's.
  const shownBackupRow = (branchId: string | null) =>
    rows.find((r) => (r.branch_id ?? null) === branchId)
      ?? (branchId ? rows.find((r) => r.branch_id === null) : undefined);

  // Backup self check-in (scanner offices). An explicit, audited admin action.
  const setBackup = async (branchId: string | null, mode: api.CaptureMode, on: boolean) => {
    const scope = branchId ?? 'default';
    if (backupOpen.current.has(scope)) return;           // a save or question is already open
    if (on === (mode === 'biometric' && !!shownBackupRow(branchId)?.self_checkin_backup)) return; // nothing to change
    // The version this change is made from: this scope's OWN row (null = none yet).
    const own = rows.find((r) => (r.branch_id ?? null) === branchId);
    backupOpen.current.add(scope);
    try {
      if (on && !(await confirm({
        title: t('capture_backup_confirm_title', 'Turn on backup self check-in?'),
        message: t('capture_backup_confirm_msg',
          'Employees can then check in from the app when the scanner is down. Every backup check-in is flagged for HR. It stays on until someone turns it off.'),
        confirmLabel: t('capture_backup_confirm_cta', 'Turn on'),
      }))) return;
      setPendingBackup((p) => ({ ...p, [scope]: on }));
      setSaving(scope);
      await api.upsertCapturePolicy(branchId, mode, undefined, {
        self_checkin_backup: on, base_updated_at: own?.updated_at ?? null,
      });
      // The row must be in the fresh read: a missing row is not proof of OFF
      // (`!!undefined === false` let "turned off" toast with nothing stored).
      if (!(await confirmSaved(branchId, (r) => !!r && !!r.self_checkin_backup === on))) { notSaved(); return; }
      toast.success(on
        ? t('capture_backup_on', 'Backup self check-in turned on')
        : t('capture_backup_off', 'Backup self check-in turned off'));
    } catch (e: any) {
      // 409 = the setting changed since this page read it: show what is in force.
      if (e?.response?.status === 409) reload();
      toast.error(getApiErrorMessage(e, t('err', 'Something went wrong')), { duration: 8000 });
    } finally {
      backupOpen.current.delete(scope);
      setPendingBackup((p) => { const next = { ...p }; delete next[scope]; return next; });
      setSaving(null);
    }
  };

  const setMode = async (branchId: string | null, mode: api.CaptureMode) => {
    setSaving(branchId ?? 'default');
    try {
      await api.upsertCapturePolicy(branchId, mode);
      if (!(await confirmSaved(branchId, (r) => r?.mode === mode))) { notSaved(); return; }
      toast.success(t('capture_saved', 'Capture mode updated'));
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
    finally { setSaving(null); }
  };

  // An office with its OWN capture setting does not follow the company
  // default — so turning backup on for the company leaves those offices
  // without it. Say so where the toggle is, or "it didn't work" is the
  // reasonable reading when someone there sees no check-in card.
  const defaultBackupOn = defaultMode === 'biometric' && !!rows.find((r) => r.branch_id === null)?.self_checkin_backup;
  // The other way round: the company default is OFF, but an office with its
  // OWN setting still has backup ON — unticking the company toggle did not turn
  // it off there, and the card must say so (HO-0mvq38s7e001-d470ae).
  const officesStillOn = !defaultBackupOn
    ? branches.filter((b) => {
      const own = rows.find((r) => r.branch_id === b.id);
      return own && own.mode === 'biometric' && !!own.self_checkin_backup;
    })
    : [];
  const officesWithoutBackup = defaultBackupOn
    ? branches.filter((b) => {
      const own = rows.find((r) => r.branch_id === b.id);
      return own && own.mode === 'biometric' && !own.self_checkin_backup;
    })
    : [];

  const modeSelect = (value: api.CaptureMode, onChange: (m: api.CaptureMode) => void, busy: boolean) => (
    <select
      className="h-9 rounded-md border border-border bg-background px-2 text-sm"
      value={value}
      disabled={!canWrite || busy}
      onChange={(e) => onChange(e.target.value as api.CaptureMode)}
    >
      {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(`capture_${o.value}`, o.label)}</option>)}
    </select>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('capture_title', 'Presence capture')}</CardTitle>
        <p className="text-xs text-muted-foreground">
          {t('capture_hint', 'How each office records presence. Offices without a scanner can use self check-in — punches follow the same rules, review and approval as scanner days.')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loadState === 'loading' && (
          <DataState variant="loading" compact message={t('capture_loading', 'Loading the capture settings…') as string} />
        )}
        {loadState === 'error' && (
          <DataState
            variant="error" compact onRetry={retryLoad} retryLabel={t('retry', 'Retry') as string}
            message={`${loadError} ${t('capture_load_unknown', 'The settings in force are not known until this loads.')}`}
          />
        )}
        {loadState === 'ready' && (<>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-4 py-3">
          <div>
            <p className="text-sm font-medium text-charcoal">{t('capture_default', 'Company default')}</p>
            <p className="text-xs text-muted-foreground">{t(`capture_${defaultMode}_hint`, MODE_OPTIONS.find((o) => o.value === defaultMode)?.hint || '')}</p>
          </div>
          {modeSelect(defaultMode, (m) => setMode(null, m), saving === 'default')}
          <BackupCheckinToggle
            mode={defaultMode} row={rows.find((r) => r.branch_id === null)} canWrite={canWrite}
            busy={saving === 'default'} pending={pendingBackup.default}
            onChange={(on) => setBackup(null, defaultMode, on)}
          />
          {officesStillOn.length > 0 && (
            <p className="w-full text-xs text-warning" data-testid="backup-still-on-offices">
              {t('capture_backup_still_on', 'Still on at {{offices}} — they have their own setting below.', {
                offices: officesStillOn.map((b) => b.name).join(', '),
              })}
            </p>
          )}
          {officesWithoutBackup.length > 0 && (
            <p className="w-full text-xs text-warning" data-testid="backup-own-setting-offices">
              {t('capture_backup_not_inherited', 'Not on at {{offices}} — they have their own setting below.', {
                offices: officesWithoutBackup.map((b) => b.name).join(', '),
              })}
            </p>
          )}
          {(defaultMode === 'self_checkin'
            || (defaultMode === 'biometric' && rows.find((r) => r.branch_id === null)?.self_checkin_backup)) && (
            <OfficeNetworkPanel
              key={`default-${rows.find((r) => r.branch_id === null)?.updated_at ?? ''}`}
              branchId={null} mode={defaultMode} canWrite={canWrite} onSaved={reload}
              row={rows.find((r) => r.branch_id === null)}
            />
          )}
        </div>
        {branches.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium text-charcoal">{b.name}</p>
              {!rows.some((r) => r.branch_id === b.id) && (
                <p className="text-xs text-muted-foreground">{t('capture_inherits', 'Uses the company default')}</p>
              )}
            </div>
            {modeSelect(modeFor(b.id), (m) => setMode(b.id, m), saving === b.id)}
            <BackupCheckinToggle
              mode={modeFor(b.id)}
              row={rows.find((r) => r.branch_id === b.id) ?? rows.find((r) => r.branch_id === null)}
              canWrite={canWrite} busy={saving === b.id} pending={pendingBackup[b.id]}
              onChange={(on) => setBackup(b.id, modeFor(b.id), on)}
            />
            {/* An office with its own self check-in setting gets its own
                network; one that inherits the default uses the default's. */}
            {(modeFor(b.id) === 'self_checkin'
              || (modeFor(b.id) === 'biometric' && rows.find((r) => r.branch_id === b.id)?.self_checkin_backup))
              && rows.some((r) => r.branch_id === b.id) && (
              <OfficeNetworkPanel
                key={`${b.id}-${rows.find((r) => r.branch_id === b.id)?.updated_at ?? ''}`}
                branchId={b.id} mode={modeFor(b.id)} canWrite={canWrite} onSaved={reload}
                row={rows.find((r) => r.branch_id === b.id)}
              />
            )}
          </div>
        ))}
        </>)}
      </CardContent>
    </Card>
  );
};

// ---- Self check-in review (batchless days) ---------------------------------------

const SelfDaysTab: React.FC<{
  nameOf: (id?: string | null) => string;
  canWrite: boolean;
  refreshKey: number;
  onGlobalChanged: () => void;
}> = ({ nameOf, canWrite, refreshKey, onGlobalChanged }) => {
  const { t } = useTranslation('attendance');
  const [days, setDays] = useState<api.AttendanceDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.listDays({ status: 'pending_review' })
      .then((rows) => setDays(rows.filter((d) => !d.batch_id)))
      .catch(() => setDays([]))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <Skeleton className="h-32 w-full" />;
  if (days.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('self_days_empty', 'No self check-in days awaiting review.')}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {days.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3 text-sm">
          <div className="min-w-0">
            <span className="font-medium">{nameOf(d.employee_id)}</span>
            <Badge variant="outline" className="ml-2 text-[10px]">{d.source === 'self' ? t('src_self', 'Self check-in')
              : d.source === 'merged' ? t('src_merged', 'Scanner + file')
              : d.source}</Badge>
            <div className="text-xs text-muted-foreground">
              {formatDate(d.work_date)} · {d.clock_in || '—'} → {d.clock_out || '—'} · {api.formatMinutes(d.net_minutes)}
              {d.potential_ot_minutes > 0 ? ` · OT ${api.formatMinutes(d.potential_ot_minutes)}` : ''}
              {!d.clock_out && d.clock_in ? ` · ${t('self_missing_out', 'still checked in / missing clock-out')}` : ''}
              {outsideOffice(d) && (
                <span className="ml-2 rounded bg-warning-soft px-2 py-0.5 text-xs text-warning" data-testid="outside-office">
                  {t('self_outside_office', 'Checked in outside the office')}
                </span>
              )}
            </div>
          </div>
          {canWrite && (
            <Button size="sm" disabled={busyId === d.id || !d.clock_out} onClick={async () => {
              setBusyId(d.id);
              try {
                const r = await api.approveDay(d.id);
                toast.success(r.overtime_request_created
                  ? t('self_approved_ot', 'Approved — overtime request created')
                  : t('self_approved', 'Approved'));
                onGlobalChanged();
              } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
              finally { setBusyId(null); }
            }}>
              {busyId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : t('self_approve', 'Approve')}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

// ---- Page ---------------------------------------------------------------------

export const AttendancePage: React.FC = () => {
  const { t } = useTranslation('attendance');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = pathname.replace(/\/$/, '');
  const { canManageEmployees, canApproveLeave, hasPermission } = useRoleAccess();
  // IT / connector administrators: the connector, without anyone's attendance.
  const canSeeConnector = hasPermission('attendance:connector:read');
  const canView = canManageEmployees || canApproveLeave;
  const canWrite = canManageEmployees;
  const nameOf = useEmployeeNames();
  // Bumped after any mutation so sibling tabs / stat cards refetch.
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((n) => n + 1), []);

  if (!canView && canSeeConnector) {
    return (
      <div className="space-y-6" data-testid="connector-only">
        <div className="border-b border-border pb-6">
          <h1 className="text-h1 text-charcoal">{t('connector_only_title', 'Attendance connector')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('connector_only_desc', 'Health, sync progress and device codes still to match. Attendance records are not shown at this access level.')}
          </p>
        </div>
        <LiveConnectorCard readOnly />
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="py-16 text-center">
        <Clock className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <h2 className="text-h2 text-charcoal">{t('no_access_title', 'Attendance review')}</h2>
        <p className="mt-1 text-muted-foreground">
          {t('no_access_desc', 'You need manager or HR access to review attendance.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6">
        <div>
          <h1 className="flex items-center gap-2 text-h1 text-charcoal">
            <Clock className="h-6 w-6 text-orange-600" />
            {t('title', 'Attendance')}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {t('subtitle', 'Review imported scanner data, resolve exceptions and approve overtime before payroll.')}
          </p>
        </div>
        {canWrite && (
          <Button className="gap-2" onClick={() => navigate(`${base}/import`)}>
            <UploadCloud className="h-4 w-4" />
            {t('import_button', 'Import attendance')}
          </Button>
        )}
      </div>

      {/* Insights leads: it is the only tab that answers a question rather
          than asking for an action, and it is the one a manager opens. */}
      <Tabs defaultValue="insights">
        <TabsList>
          <TabsTrigger value="insights">{t('tab_insights', 'Insights')}</TabsTrigger>
          <TabsTrigger value="batches">{t('tab_batches', 'Batches')}</TabsTrigger>
          <TabsTrigger value="overtime">{t('tab_overtime', 'Overtime')}</TabsTrigger>
          <TabsTrigger value="exceptions">{t('tab_exceptions', 'Exceptions')}</TabsTrigger>
          <TabsTrigger value="self">{t('tab_self', 'Self check-ins')}</TabsTrigger>
          {canWrite && <TabsTrigger value="policy">{t('tab_policy', 'Policy')}</TabsTrigger>}
        </TabsList>

        <TabsContent value="insights" className="mt-5">
          {canWrite && (
            <div className="mb-5">
              <LiveConnectorCard />
            </div>
          )}
          <AttendanceInsightsTab />
        </TabsContent>
        <TabsContent value="batches" className="mt-5">
          <BatchesTab nameOf={nameOf} canWrite={canWrite} refreshKey={refreshKey} onGlobalChanged={bump} />
        </TabsContent>
        <TabsContent value="overtime" className="mt-5">
          <OvertimeTab nameOf={nameOf} canWrite={canWrite} refreshKey={refreshKey} onGlobalChanged={bump} />
        </TabsContent>
        <TabsContent value="exceptions" className="mt-5">
          <ExceptionsTab nameOf={nameOf} canWrite={canWrite} refreshKey={refreshKey} onGlobalChanged={bump} />
        </TabsContent>
        <TabsContent value="self" className="mt-5">
          {/* Line managers approve their direct reports' self-checked days;
              the server confines them to that team and refuses anyone's own day. */}
          <SelfDaysTab nameOf={nameOf} canWrite={canWrite || canApproveLeave} refreshKey={refreshKey} onGlobalChanged={bump} />
        </TabsContent>
        {canWrite && (
          <TabsContent value="policy" className="mt-5">
            <PolicyTab canWrite={canWrite} />
            <div className="mt-5">
              <CaptureModesCard canWrite={canWrite} />
            </div>
            <div className="mt-5">
              <UploadReminderCard canWrite={canWrite} />
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default AttendancePage;
